import React, { useMemo, useState } from 'react';
import { Alert, Button, Divider, Drawer, Layout, List, Menu, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  BookOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  DeliveredProcedureOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  GiftOutlined,
  InboxOutlined,
  MenuOutlined,
  MessageOutlined,
  PartitionOutlined,
  SafetyOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  SwapOutlined,
  TagOutlined,
  TeamOutlined,
  ToolOutlined,
  TruckOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { defaultDocPageKey, resolveDocPages } from './content/resolveDocPages';
import type { DocPage } from './types';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import type { AppLocale } from '../../i18n/types';
import { isAppLocale } from '../../i18n/types';

const { Header, Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const submenuKeys = [
  'pos',
  'appointments_menu',
  'clients_menu',
  'inventory',
  'purchases_expenses_menu',
  'accounting',
  'services',
  'gestion',
  'activite',
];

function buildMenuItems(t: (key: string) => string): MenuProps['items'] {
  return [
    {
      key: 'pos',
      icon: <ShopOutlined />,
      label: t('menu.transactions'),
      children: [
        { key: '/pos/transaction', icon: <ShopOutlined />, label: t('menu.newTransaction') },
        { key: '/manufacturing/orders', icon: <ToolOutlined />, label: t('menu.manufacturingOrders') },
        { key: '/pos/prestations-history', icon: <ClockCircleOutlined />, label: t('menu.prestationsHistory') },
        { key: '/pos/sales-history', icon: <ShoppingCartOutlined />, label: t('menu.productsHistory') },
        { key: '/pos/reports', icon: <BarChartOutlined />, label: t('menu.reports') },
      ],
    },
    { key: '/marketing', icon: <GiftOutlined />, label: t('menu.marketing') },
    { key: '/messages-clients', icon: <MessageOutlined />, label: t('menu.messagesClients') },
    {
      key: 'appointments_menu',
      icon: <CalendarOutlined />,
      label: t('menu.appointments'),
      children: [
        { key: '/appointments', icon: <CalendarOutlined />, label: t('menu.calendar') },
        { key: '/appointment-requests', icon: <BellOutlined />, label: t('menu.appointmentRequests') },
      ],
    },
    {
      key: 'clients_menu',
      icon: <UserOutlined />,
      label: t('menu.clients'),
      children: [
        { key: '/clients', icon: <UserOutlined />, label: t('menu.clients') },
        { key: '/payment-plans', icon: <DollarOutlined />, label: t('menu.paymentPlans') },
      ],
    },
    { key: '/referents', icon: <TeamOutlined />, label: t('menu.referents') },
    { key: '/dashboard', icon: <BarChartOutlined />, label: t('menu.dashboard') },
    {
      key: 'inventory',
      icon: <InboxOutlined />,
      label: t('menu.stock'),
      children: [
        { key: '/inventory/products', icon: <AppstoreOutlined />, label: t('menu.products') },
        { key: '/inventory/categories', icon: <TagOutlined />, label: t('menu.productCategories') },
        { key: '/inventory/movements', icon: <SwapOutlined />, label: t('menu.movements') },
        { key: '/inventory/external-dispatches', icon: <DeliveredProcedureOutlined />, label: t('menu.externalDispatches') },
        { key: '/inventory/external-destinations', icon: <ShopOutlined />, label: t('menu.externalDestinations') },
        { key: '/inventory/audit', icon: <SafetyOutlined />, label: t('menu.physicalInventories') },
        { key: '/inventory/dashboard', icon: <BarChartOutlined />, label: t('menu.stockReports') },
      ],
    },
    {
      key: 'purchases_expenses_menu',
      icon: <TruckOutlined />,
      label: t('menu.purchasesExpenses'),
      children: [
        { key: '/suppliers', icon: <TruckOutlined />, label: t('menu.suppliers') },
        { key: '/suppliers/deliveries', icon: <DeliveredProcedureOutlined />, label: t('menu.deliveries') },
        { key: '/expenses', icon: <ShoppingCartOutlined />, label: t('menu.expenses') },
        { key: '/expense-notes', icon: <FileTextOutlined />, label: t('menu.expenseNotes') },
      ],
    },
    {
      key: 'accounting',
      icon: <BankOutlined />,
      label: t('menu.accounting'),
      children: [
        { key: '/accounting/dashboard', icon: <BarChartOutlined />, label: t('menu.accountingDashboard') },
        { key: '/accounting/journals', icon: <BookOutlined />, label: t('menu.journals') },
        { key: '/accounting/tax-returns', icon: <FileDoneOutlined />, label: t('menu.taxReturns') },
      ],
    },
    {
      key: 'services',
      icon: <ToolOutlined />,
      label: t('menu.services'),
      children: [
        { key: '/services', icon: <ToolOutlined />, label: t('menu.services') },
        { key: '/services/categories', icon: <TagOutlined />, label: t('menu.serviceCategories') },
      ],
    },
    {
      key: 'gestion',
      icon: <TeamOutlined />,
      label: t('menu.employeesPayroll'),
      children: [
        { key: '/employees', icon: <TeamOutlined />, label: t('menu.employees') },
        { key: '/settings/role-profiles', icon: <SafetyOutlined />, label: t('menu.roleProfiles') },
        { key: '/payrolls', icon: <DollarOutlined />, label: t('menu.payrolls') },
        { key: '/performance-indicators', icon: <BarChartOutlined />, label: t('menu.performanceIndicators') },
      ],
    },
    {
      key: 'activite',
      icon: <FileTextOutlined />,
      label: t('menu.activity'),
      children: [
        { key: '/activity/reports', icon: <FileTextOutlined />, label: t('menu.activityReports') },
        { key: '/activity/opportunities', icon: <PartitionOutlined />, label: t('menu.opportunities') },
        { key: '/settings/pipeline', icon: <SettingOutlined />, label: t('menu.pipelineConfig') },
      ],
    },
    { key: '/mon-paiement', icon: <CreditCardOutlined />, label: t('menu.licencePayment') },
  ];
}

function renderDocPage(
  page: DocPage,
  onNavigate: (path: string) => void,
  docPages: Record<string, DocPage>,
  t: (key: string) => string
) {
  return (
    <div>
      <Title level={3} style={{ marginTop: 0 }}>
        {page.title}
      </Title>

      <Paragraph>
        <Tag color="blue">{t('shell.appRoute')}</Tag> <Text code>{page.path}</Text>
      </Paragraph>

      <Title level={4}>{t('shell.whatIsIt')}</Title>
      <Paragraph>{page.definition}</Paragraph>

      <Title level={4}>{t('shell.whatFor')}</Title>
      <Paragraph>{page.utility}</Paragraph>

      <Title level={4}>{t('shell.relatedModules')}</Title>
      <List
        size="small"
        dataSource={page.relatedModules}
        renderItem={(item) => (
          <List.Item>
            <div>
              {docPages[item.path] ? (
                <ButtonLink onClick={() => onNavigate(item.path)}>{item.label}</ButtonLink>
              ) : (
                <Text strong>{item.label}</Text>
              )}
              <div>
                <Text type="secondary">{item.relation}</Text>
              </div>
            </div>
          </List.Item>
        )}
      />

      <Divider />

      <Title level={5}>{t('shell.whenToUse')}</Title>
      <List size="small" dataSource={page.whenToUse} renderItem={(item) => <List.Item>{item}</List.Item>} />

      <Title level={5}>{t('shell.keyActions')}</Title>
      <List size="small" dataSource={page.keyActions} renderItem={(item) => <List.Item>{item}</List.Item>} />

      <Title level={5}>{t('shell.workflow')}</Title>
      <List
        size="small"
        dataSource={page.workflow}
        renderItem={(item, index) => (
          <List.Item>
            <Text>
              {page.workflow.length > 1 && !/^\d+\./.test(item) ? `${index + 1}. ` : ''}
              {item}
            </Text>
          </List.Item>
        )}
      />

      {page.importantNotes && page.importantNotes.length > 0 ? (
        <>
          <Title level={5}>{t('shell.importantNotes')}</Title>
          <List
            size="small"
            dataSource={page.importantNotes}
            renderItem={(item) => (
              <List.Item>
                <Text type="warning">{item}</Text>
              </List.Item>
            )}
          />
        </>
      ) : null}

      {page.checks && page.checks.length > 0 ? (
        <>
          <Title level={5}>{t('shell.checks')}</Title>
          <List size="small" dataSource={page.checks} renderItem={(item) => <List.Item>{item}</List.Item>} />
        </>
      ) : null}
    </div>
  );
}

function ButtonLink({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: '#1677ff',
        cursor: 'pointer',
        fontWeight: 600,
        textAlign: 'left',
      }}
    >
      {children}
    </button>
  );
}

const Documentation: React.FC = () => {
  const { t, i18n } = useAppTranslation(['documentation']);
  const locale: AppLocale = isAppLocale(i18n.language) ? i18n.language : 'fr';
  const docPages = useMemo(() => resolveDocPages(locale), [locale]);
  const menuItems = useMemo(() => buildMenuItems(t), [t]);

  const [selectedSection, setSelectedSection] = useState(defaultDocPageKey);
  const [openKeys, setOpenKeys] = useState<string[]>(submenuKeys);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const selectedPage = useMemo(() => {
    return docPages[selectedSection] ?? docPages[defaultDocPageKey];
  }, [docPages, selectedSection]);

  const selectSection = (key: string) => {
    if (!docPages[key]) return;
    setSelectedSection(key);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleNavigate = (path: string) => {
    if (!docPages[path]) return;
    selectSection(path);
    const parentKey = submenuKeys.find((key) => {
      const item = menuItems?.find((m) => m && typeof m === 'object' && 'key' in m && m.key === key);
      if (!item || typeof item !== 'object' || !('children' in item) || !Array.isArray(item.children)) return false;
      return item.children.some((child) => child && typeof child === 'object' && 'key' in child && child.key === path);
    });
    if (parentKey) {
      setOpenKeys((prev) => (prev.includes(parentKey) ? prev : [...prev, parentKey]));
    }
  };

  const sideMenu = (
    <Menu
      mode="inline"
      selectedKeys={[selectedSection]}
      openKeys={openKeys}
      style={{ height: '100%', borderRight: 0 }}
      items={menuItems}
      onOpenChange={(keys) => setOpenKeys(keys as string[])}
      onClick={({ key }) => selectSection(key)}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <Header
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: isMobile ? '0 12px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16, minWidth: 0 }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: 'white', fontSize: 18 }} />}
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('shell.openMenuAria')}
            />
          )}
          <BookOutlined style={{ fontSize: isMobile ? 20 : 24, color: 'white', flexShrink: 0 }} />
          <Title
            level={isMobile ? 5 : 3}
            style={{
              color: 'white',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('shell.title')}
          </Title>
        </div>
        {!isMobile && (
          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, flexShrink: 0 }}>
            {t('shell.subtitle')}
          </Text>
        )}
      </Header>

      <Layout style={{ flex: 1, minHeight: 0 }}>
        {!isMobile && (
          <Sider
            width={280}
            style={{
              background: 'white',
              boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
              overflow: 'auto',
              height: 'calc(100vh - 64px)',
              position: 'sticky',
              top: 64,
            }}
          >
            {sideMenu}
          </Sider>
        )}

        <Content
          style={{
            padding: isMobile ? 12 : 24,
            overflow: 'auto',
            width: '100%',
            minWidth: 0,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 8,
              padding: isMobile ? 16 : 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              width: '100%',
              minWidth: 0,
            }}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('shell.howToReadTitle')}
              description={t('shell.howToReadBody')}
            />
            {renderDocPage(selectedPage, handleNavigate, docPages, t)}
          </div>
        </Content>
      </Layout>

      <Drawer
        title={t('shell.toc')}
        placement="left"
        width={280}
        open={isMobile && mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        styles={{ body: { padding: 0 } }}
        destroyOnClose={false}
      >
        {sideMenu}
      </Drawer>
    </Layout>
  );
};

export default Documentation;
